import React, { useState, useEffect } from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

// Contexts
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { GMBProvider } from "./contexts/GMBContext";

// Components
import ProtectedRoute from "./components/ProtectedRoute";
import DashboardLayout from "./components/DashboardLayout";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import PricingPage from "./pages/PricingPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import DashboardHome from "./pages/DashboardHome";
import PostGeneratorPage from "./pages/PostGeneratorPage";
import ReviewReplyPage from "./pages/ReviewReplyPage";
import AuditPage from "./pages/AuditPage";
import ScheduledPostsPage from "./pages/ScheduledPostsPage";
import AutoPostSettingsPage from "./pages/AutoPostSettingsPage";
import AutoReplySettingsPage from "./pages/AutoReplySettingsPage";
import AnalyticsDashboardPage from "./pages/AnalyticsDashboardPage";
import LocationsPage from "./pages/LocationsPage";
import CreativeTemplatePage from "./pages/CreativeTemplatePage";
import TeamPage from "./pages/TeamPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import ReferralDashboardPage from "./pages/ReferralDashboardPage";
import ReferralWalletPage from "./pages/ReferralWalletPage";
import LeaderboardPage from "./pages/LeaderboardPage";

// Admin Pages
import AdminLoginPage from "./pages/admin/AdminLoginPage";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboardPage from "./pages/admin/AdminDashboardPage";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import AdminPostsPage from "./pages/admin/AdminPostsPage";
import AdminTransactionsPage from "./pages/admin/AdminTransactionsPage";
import AdminMembershipPage from "./pages/admin/AdminMembershipPage";
import AdminSettingsPage from "./pages/admin/AdminSettingsPage";
import AdminPaymentGatewayPage from "./pages/admin/AdminPaymentGatewayPage";
import AdminProfilePage from "./pages/admin/AdminProfilePage";
import AdminMaintenancePage from "./pages/admin/AdminMaintenancePage";
import AdminAlertBannerPage from "./pages/admin/AdminAlertBannerPage";
import AdminSmtpPage from "./pages/admin/AdminSmtpPage";
import AdminTemplatePage from "./pages/admin/AdminTemplatePage";
import AdminReferralSettingsPage from "./pages/admin/AdminReferralSettingsPage";
import AdminReferralListPage from "./pages/admin/AdminReferralListPage";
import AdminReferralPayoutsPage from "./pages/admin/AdminReferralPayoutsPage";
import AdminEmailNotificationsPage from "./pages/admin/AdminEmailNotificationsPage";
import AdminPagesPage from "./pages/admin/AdminPagesPage";
import AdminSiteSettingsPage from "./pages/admin/AdminSiteSettingsPage";
import AdminFaqsPage from "./pages/admin/AdminFaqsPage";
import Admin2FAUsersPage from "./pages/admin/Admin2FAUsersPage";
import AdminAuditLogPage from "./pages/admin/AdminAuditLogPage";
import AdminApiKeysPage from "./pages/admin/AdminApiKeysPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import MaintenancePage from "./pages/MaintenancePage";
import UserSettingsPage from "./pages/UserSettingsPage";
import BillingPage from "./pages/BillingPage";
import PublicPage from "./pages/PublicPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import { siteAPI } from "./lib/api";

// Admin Maintenance Banner
const MaintenanceBanner = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [maintenanceOn, setMaintenanceOn] = useState(false);

  const isAdmin = user?.is_admin || (() => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('admin_user') || '{}');
      return adminUser?.is_admin === true;
    } catch { return false; }
  })();

  useEffect(() => {
    if (!isAdmin) { setMaintenanceOn(false); return; }
    const check = async () => {
      try {
        const res = await siteAPI.getStatus();
        setMaintenanceOn(res.data.maintenance_enabled || false);
      } catch { /* ignore */ }
    };
    check();
  }, [isAdmin, location.pathname]);

  if (!maintenanceOn || !isAdmin) return null;
  // Don't show on admin pages or maintenance page itself
  if (location.pathname.startsWith('/gmbadmin') || location.pathname === '/maintenance') return null;

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-[9999] bg-[#F9AB00] text-[#202124] px-4 py-2.5 text-center text-sm font-medium shadow-md" data-testid="admin-maintenance-banner">
        <span className="inline-flex items-center gap-2">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          Maintenance Mode is ON — Regular users see the maintenance page. You can browse because you're an admin.
          <a href="/gmbadmin/settings/maintenance" className="underline font-semibold ml-1 hover:opacity-80">Turn Off</a>
        </span>
      </div>
      <div className="h-10" />
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <GMBProvider>
          <BrowserRouter>
            <Toaster 
              position="top-right" 
              toastOptions={{
                style: {
                  background: '#202124',
                  color: '#fff',
                  border: '1px solid #DADCE0',
                  borderRadius: '8px',
                },
              }}
            />
            <MaintenanceBanner />
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/payment-success" element={<PaymentSuccessPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/maintenance" element={<MaintenancePage />} />
              <Route path="/invite/:token" element={<AcceptInvitePage />} />
              <Route path="/leaderboard" element={<LeaderboardPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/page/:slug" element={<PublicPage />} />

              {/* Protected Dashboard Routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <DashboardHome />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/post-generator"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <PostGeneratorPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/review-reply"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <ReviewReplyPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/audit"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <AuditPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/schedule"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <ScheduledPostsPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/auto-post"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <AutoPostSettingsPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/auto-reply"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <AutoReplySettingsPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/settings"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <UserSettingsPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/billing"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <BillingPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/analytics"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <AnalyticsDashboardPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/locations"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <LocationsPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/creative-templates"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <CreativeTemplatePage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/team"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <TeamPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/referrals"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <ReferralDashboardPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/referrals/wallet"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <ReferralWalletPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              {/* Admin Routes */}
              <Route path="/gmbadmin" element={<AdminLoginPage />} />
              <Route
                path="/gmbadmin/dashboard"
                element={
                  <AdminLayout>
                    <AdminDashboardPage />
                  </AdminLayout>
                }
              />
              <Route
                path="/gmbadmin/users"
                element={
                  <AdminLayout>
                    <AdminUsersPage />
                  </AdminLayout>
                }
              />
              <Route
                path="/gmbadmin/posts"
                element={
                  <AdminLayout>
                    <AdminPostsPage />
                  </AdminLayout>
                }
              />
              <Route
                path="/gmbadmin/transactions"
                element={
                  <AdminLayout>
                    <AdminTransactionsPage />
                  </AdminLayout>
                }
              />
              <Route
                path="/gmbadmin/membership"
                element={
                  <AdminLayout>
                    <AdminMembershipPage />
                  </AdminLayout>
                }
              />
              <Route
                path="/gmbadmin/templates"
                element={
                  <AdminLayout>
                    <AdminTemplatePage />
                  </AdminLayout>
                }
              />
              <Route
                path="/gmbadmin/referrals/settings"
                element={
                  <AdminLayout>
                    <AdminReferralSettingsPage />
                  </AdminLayout>
                }
              />
              <Route
                path="/gmbadmin/referrals/list"
                element={
                  <AdminLayout>
                    <AdminReferralListPage />
                  </AdminLayout>
                }
              />
              <Route
                path="/gmbadmin/referrals/payouts"
                element={
                  <AdminLayout>
                    <AdminReferralPayoutsPage />
                  </AdminLayout>
                }
              />
              <Route
                path="/gmbadmin/settings/email-notifications"
                element={
                  <AdminLayout>
                    <AdminEmailNotificationsPage />
                  </AdminLayout>
                }
              />
              <Route
                path="/gmbadmin/settings/payment-gateway"
                element={
                  <AdminLayout>
                    <AdminPaymentGatewayPage />
                  </AdminLayout>
                }
              />
              <Route
                path="/gmbadmin/settings/maintenance"
                element={
                  <AdminLayout>
                    <AdminMaintenancePage />
                  </AdminLayout>
                }
              />
              <Route
                path="/gmbadmin/settings/alert"
                element={
                  <AdminLayout>
                    <AdminAlertBannerPage />
                  </AdminLayout>
                }
              />
              <Route
                path="/gmbadmin/settings/smtp"
                element={
                  <AdminLayout>
                    <AdminSmtpPage />
                  </AdminLayout>
                }
              />
              <Route
                path="/gmbadmin/profile"
                element={
                  <AdminLayout>
                    <AdminProfilePage />
                  </AdminLayout>
                }
              />
              <Route
                path="/gmbadmin/pages"
                element={
                  <AdminLayout>
                    <AdminPagesPage />
                  </AdminLayout>
                }
              />
              <Route
                path="/gmbadmin/faqs"
                element={
                  <AdminLayout>
                    <AdminFaqsPage />
                  </AdminLayout>
                }
              />
              <Route
                path="/gmbadmin/settings/site"
                element={
                  <AdminLayout>
                    <AdminSiteSettingsPage />
                  </AdminLayout>
                }
              />
              <Route
                path="/gmbadmin/security/2fa-users"
                element={
                  <AdminLayout>
                    <Admin2FAUsersPage />
                  </AdminLayout>
                }
              />
              <Route
                path="/gmbadmin/security/audit-log"
                element={
                  <AdminLayout>
                    <AdminAuditLogPage />
                  </AdminLayout>
                }
              />
              <Route
                path="/gmbadmin/security/api-keys"
                element={
                  <AdminLayout>
                    <AdminApiKeysPage />
                  </AdminLayout>
                }
              />
            </Routes>
          </BrowserRouter>
        </GMBProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
